import {
  // Existing imports from Heroicons
  BuildingOfficeIcon,
  BuildingLibraryIcon,
  ComputerDesktopIcon,
  FilmIcon,
  MusicalNoteIcon,
  BeakerIcon,
  CubeIcon,
  BuildingStorefrontIcon,
  BuildingOffice2Icon,
  PresentationChartBarIcon,
  BookOpenIcon,
  WrenchScrewdriverIcon,
  CloudIcon, // For Cold Storage
  TvIcon, // For Showroom
  HeartIcon, // For Medical Office
  ArrowsRightLeftIcon, // For Flex Space
  ServerIcon, // For Data Center
  TruckIcon, // For Logistics Center
  CurrencyDollarIcon, // For Retail Kiosk
  AcademicCapIcon, // For Training Room
  CpuChipIcon, // For Tech Lab
  ShoppingBagIcon, // For Pop-up Shop
  ArchiveBoxIcon, // For Storage Unit
  SunIcon, // For Outdoor Workspace
  DeviceTabletIcon, // For Co-working Pod
  ChartBarIcon, // For Executive Suite
  FireIcon, // For Commercial Kitchen (alternative)
  EyeIcon, // For Photography Studio (enhanced)
  RectangleGroupIcon, // For Mixed-Use Space
} from "@heroicons/react/24/outline";

const workspaceTypes = [
  {
    value: null,
    title: "",
    description: "",
    hoverDescription: "",
    icon: null,
  },
  {
    value: 1,
    title: "Private Office",
    description: "Dedicated private offices for individuals or small teams.",
    hoverDescription:
      "Lockable private spaces with desks, storage, and meeting areas.",
    icon: BuildingOfficeIcon,
    category: "Office",
  },
  {
    value: 2,
    title: "Shared Office",
    description: "Open-plan workspaces shared with other professionals.",
    hoverDescription:
      "Cost-effective solution with shared amenities and networking opportunities.",
    icon: BuildingLibraryIcon,
    category: "Office",
  },
  {
    value: 3,
    title: "Coworking Desk",
    description: "Flexible desk space in a shared environment.",
    hoverDescription:
      "Hot desks with access to communal facilities and networking events.",
    icon: ComputerDesktopIcon,
    category: "Shared Space",
  },
  {
    value: 4,
    title: "Enterprise Suite",
    description: "Enterprise-level office spaces for larger teams.",
    hoverDescription:
      "Customizable layouts with dedicated amenities for larger organizations.",
    icon: BuildingOffice2Icon,
    category: "Office",
  },
  {
    value: 5,
    title: "Serviced Office",
    description:
      "Fully furnished and equipped office spaces with flexible terms.",
    hoverDescription:
      "All-inclusive packages with utilities, internet, and office services.",
    icon: BuildingOffice2Icon,
    category: "Office",
  },
  {
    value: 6,
    title: "Sublet Office Space",
    description: "Sublet office spaces for short-term use.",
    hoverDescription:
      "Flexible arrangements for businesses needing temporary office space.",
    icon: ArrowsRightLeftIcon,
    category: "Office",
  },
  {
    value: 7,
    title: "Creative Studio",
    description: "Spaces designed for creative professionals and artists.",
    hoverDescription:
      "Natural light, high ceilings, and specialized equipment for creative work.",
    icon: FilmIcon,
    category: "Creative",
  },
  {
    value: 8,
    title: "Music Studio",
    description: "Acoustically treated spaces for recording and rehearsal.",
    hoverDescription: "Soundproof rooms with professional audio equipment.",
    icon: MusicalNoteIcon,
    category: "Creative",
  },
  {
    value: 9,
    title: "Photography Studio",
    description: "Spaces equipped for professional photography.",
    hoverDescription: "Backdrops, lighting equipment, and shooting spaces.",
    icon: EyeIcon, // Enhanced from CameraIcon to EyeIcon for better visual
    category: "Creative",
  },
  {
    value: 10,
    title: "Science Lab",
    description: "Specialized laboratories for research and development.",
    hoverDescription:
      "Equipped with lab benches, fume hoods, and specialized equipment.",
    icon: BeakerIcon,
    category: "Specialized",
  },
  {
    value: 11,
    title: "Commercial Kitchen",
    description: "Fully-equipped kitchens for food businesses.",
    hoverDescription:
      "Industrial appliances, prep areas, and storage for food service.",
    icon: FireIcon, // Changed to FireIcon (stove/heat representation)
    category: "Food & Beverage",
  },
  {
    value: 12,
    title: "Warehouse",
    description: "Large storage and distribution spaces.",
    hoverDescription:
      "High ceilings, loading docks, and open floor plans for inventory.",
    icon: CubeIcon,
    category: "Industrial",
  },
  {
    value: 13,
    title: "Shop Front",
    description: "Retail spaces with street visibility.",
    hoverDescription: "Storefronts with display windows and customer areas.",
    icon: BuildingStorefrontIcon,
    category: "Retail",
  },
  {
    value: 14,
    title: "Entire Floor (Office)",
    description: "Complete office floors for larger organizations.",
    hoverDescription:
      "Customizable layouts for departments or entire companies.",
    icon: BuildingOffice2Icon,
    category: "Office",
  },
  {
    value: 15,
    title: "Coworking Space",
    description: "Flexible memberships for individuals and teams.",
    hoverDescription: "Community spaces with hot desks and shared amenities.",
    icon: ComputerDesktopIcon,
    category: "Shared Space",
  },
  {
    value: 16,
    title: "Workshop",
    description: "Spaces for fabrication and hands-on work.",
    hoverDescription: "Tool benches, equipment, and workspace for makers.",
    icon: WrenchScrewdriverIcon,
    category: "Industrial",
  },
  {
    value: 17,
    title: "Meeting Room",
    description: "Professional spaces for presentations and collaboration.",
    hoverDescription: "Equipped with AV systems and conference tables.",
    icon: PresentationChartBarIcon,
    category: "Shared Space",
  },
  {
    value: 18,
    title: "Study Space",
    description: "Quiet areas for focused individual work.",
    hoverDescription: "Peaceful environments with desks and reading areas.",
    icon: BookOpenIcon,
    category: "Education",
  },

  // NEW COMMERCIAL SPACE TYPES
  {
    value: 19,
    title: "Industrial Warehouse",
    description: "Large-scale storage and distribution facilities.",
    hoverDescription:
      "High ceilings, loading docks, heavy power supply, and forklift access.",
    icon: CubeIcon,
    category: "Industrial",
    commercialGrade: true,
  },
  {
    value: 20,
    title: "Cold Storage",
    description: "Temperature-controlled warehouse space.",
    hoverDescription:
      "Refrigerated/frozen storage for perishable goods and pharmaceuticals.",
    icon: CloudIcon, // Represents cold/refrigeration
    category: "Industrial",
    commercialGrade: true,
  },
  {
    value: 21,
    title: "Showroom",
    description: "Display spaces with customer viewing areas.",
    hoverDescription:
      "Combination of product display, meeting areas, and customer interaction zones.",
    icon: TvIcon, // Represents display/screens
    category: "Retail",
    commercialGrade: true,
  },
  {
    value: 22,
    title: "Medical Office",
    description: "Healthcare professional and clinical spaces.",
    hoverDescription:
      "HIPAA compliant, exam rooms, waiting areas, and medical equipment ready.",
    icon: HeartIcon, // Medical/health representation
    category: "Professional",
    commercialGrade: true,
  },
  {
    value: 23,
    title: "Flex Space",
    description: "Combination office and warehouse/showroom.",
    hoverDescription:
      "Office front with warehouse, workshop, or showroom in the back.",
    icon: ArrowsRightLeftIcon, // Represents flexibility/combination
    category: "Mixed-Use",
    commercialGrade: true,
  },
  {
    value: 24,
    title: "Data Center",
    description: "Secure facilities for server and IT infrastructure.",
    hoverDescription:
      "Redundant power, cooling, security, and high-speed connectivity.",
    icon: ServerIcon,
    category: "Technology",
    commercialGrade: true,
  },
  {
    value: 25,
    title: "Logistics Center",
    description: "Distribution and fulfillment facilities.",
    hoverDescription:
      "Cross-docking, sorting systems, and last-mile delivery integration.",
    icon: TruckIcon,
    category: "Industrial",
    commercialGrade: true,
  },
  {
    value: 26,
    title: "Retail Kiosk",
    description: "Small retail spaces in high-traffic areas.",
    hoverDescription:
      "Mall, airport, or station kiosks for quick-service retail.",
    icon: CurrencyDollarIcon, // Represents retail/sales
    category: "Retail",
    commercialGrade: true,
  },
  {
    value: 27,
    title: "Training Room",
    description: "Spaces designed for workshops and education.",
    hoverDescription:
      "Classroom-style setup with AV equipment and presentation tools.",
    icon: AcademicCapIcon,
    category: "Education",
    commercialGrade: true,
  },
  {
    value: 28,
    title: "Tech Lab",
    description: "Spaces for technology development and testing.",
    hoverDescription:
      "Equipment for prototyping, electronics, and software development.",
    icon: CpuChipIcon,
    category: "Technology",
    commercialGrade: true,
  },
  {
    value: 29,
    title: "Pop-up Shop",
    description: "Temporary retail spaces for seasonal or promotional use.",
    hoverDescription:
      "Short-term leases for product launches, holiday sales, or brand activations.",
    icon: ShoppingBagIcon,
    category: "Retail",
    commercialGrade: true,
  },
  {
    value: 30,
    title: "Storage Unit",
    description: "Secure storage spaces for business inventory.",
    hoverDescription:
      "Climate-controlled storage for documents, inventory, or equipment.",
    icon: ArchiveBoxIcon,
    category: "Storage",
    commercialGrade: true,
  },
  {
    value: 31,
    title: "Outdoor Workspace",
    description: "Open-air working environments.",
    hoverDescription:
      "Garden offices, rooftop workspaces, or covered outdoor areas.",
    icon: SunIcon,
    category: "Specialized",
    commercialGrade: false,
  },
  {
    value: 32,
    title: "Co-working Pod",
    description: "Small, private workspaces within shared environments.",
    hoverDescription:
      "Soundproof individual pods for focused work in busy areas.",
    icon: DeviceTabletIcon, // Represents compact/tech-forward
    category: "Shared Space",
    commercialGrade: false,
  },
  {
    value: 33,
    title: "Executive Suite",
    description: "Premium office spaces with enhanced amenities.",
    hoverDescription:
      "Luxury finishes, premium locations, and exclusive services.",
    icon: ChartBarIcon, // Represents executive/business
    category: "Office",
    commercialGrade: true,
  },
  {
    value: 34,
    title: "Mixed-Use Space",
    description: "Combination of retail, office, and residential.",
    hoverDescription:
      "Live-work-play environments with multiple business types.",
    icon: RectangleGroupIcon,
    category: "Mixed-Use",
    commercialGrade: true,
  },
];

// Optional: Group by category for UI organization
export const workspaceTypesByCategory = {
  Office: [1, 2, 4, 5, 6, 14, 33],
  "Shared Space": [3, 15, 17, 32],
  Industrial: [12, 16, 19, 20, 25],
  Retail: [13, 21, 26, 29],
  Creative: [7, 8, 9],
  Specialized: [10, 11, 31],
  Professional: [22, 27, 28],
  Technology: [24, 28],
  "Mixed-Use": [23, 34],
  Storage: [30],
  Education: [18, 27],
  "Food & Beverage": [11],
};

export default workspaceTypes;
